import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Checkbox,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import apiAxiosInstance from '../../../utils/axiosInstance';
import AddStudentDialog from './AddStudentDialog';

interface Student {
  _id: string;
  firstName?: string;
  lastName?: string;
  gradeLevel?: string | number;
}

interface Group {
  _id: string;
  name: string;
  students?: (Student | string)[];
}

interface GroupEditDialogProps {
  open: boolean;
  onClose: () => void;
  group: Group | null;
  allStudents: Student[];
  onSaved: (groupId: string) => void;
}

const GroupEditDialog: React.FC<GroupEditDialogProps> = ({
  open,
  onClose,
  group,
  allStudents,
  onSaved,
}) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  // Local students list — starts from props but grows when new students are added
  const [localStudents, setLocalStudents] = useState<Student[]>([]);

  // Sync local students list from props
  useEffect(() => {
    setLocalStudents(allStudents);
  }, [allStudents]);

  // Initialize selected students when group or dialog opens
  useEffect(() => {
    if (!group) {
      setSelectedIds(new Set());
      return;
    }

    const currentIds = new Set<string>(
      (group.students || []).map((s) =>
        typeof s === 'string' ? s : s._id
      )
    );
    setSelectedIds(currentIds);
    setSearch('');
    setError(null);
  }, [group, open]);

  const toggleStudent = (studentId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!group) return;
    setSaving(true);
    setError(null);
    try {
      await apiAxiosInstance.post(`/api/groups/${group._id}/assign-students`, {
        studentIds: Array.from(selectedIds),
      });
      onSaved(group._id);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to save group members.');
    } finally {
      setSaving(false);
    }
  };

  const handleNewStudentAdded = (newStudent: Student) => {
    // Add to local list and auto-select them
    setLocalStudents((prev) => {
      if (prev.find((s) => s._id === newStudent._id)) return prev;
      return [...prev, newStudent];
    });
    setSelectedIds((prev) => new Set([...prev, newStudent._id]));
    setAddStudentOpen(false);
  };

  const filteredStudents = localStudents.filter((s) => {
    if (!search.trim()) return true;
    const name = `${s.firstName || ''} ${s.lastName || ''}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const selectedCount = selectedIds.size;

  const getStudentName = (s: Student) =>
    `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unnamed';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
            <GroupIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              Edit Group Members
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {group?.name}
            </Typography>
          </Box>
          <Chip
            label={`${selectedCount} selected`}
            size="small"
            color="primary"
            sx={{ ml: 'auto' }}
          />
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, pb: 1, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search students..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={() => setAddStudentOpen(true)}
            sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}
          >
            New Student
          </Button>
        </Box>

        {error && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {localStudents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No students available
            </Typography>
          </Box>
        ) : filteredStudents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No students match your search
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 360, overflow: 'auto', px: 1 }}>
            {filteredStudents.map((student) => {
              const isIn = selectedIds.has(student._id);
              return (
                <ListItem key={student._id} disablePadding>
                  <ListItemButton
                    onClick={() => toggleStudent(student._id)}
                    selected={isIn}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.50',
                        '&:hover': { backgroundColor: 'primary.100' },
                      },
                    }}
                  >
                    <Checkbox
                      edge="start"
                      checked={isIn}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                      sx={{ mr: 0.5 }}
                    />
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                        {(student.firstName?.[0] || '') + (student.lastName?.[0] || '')}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getStudentName(student)}
                      secondary={
                        student.gradeLevel ? `Grade ${student.gradeLevel}` : undefined
                      }
                      primaryTypographyProps={{ fontWeight: isIn ? 600 : 400 }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Button
            size="small"
            onClick={() => setSelectedIds(new Set(localStudents.map((s) => s._id)))}
            disabled={saving}
          >
            Select All
          </Button>
          <Button
            size="small"
            onClick={() => setSelectedIds(new Set())}
            disabled={saving}
            sx={{ ml: 1 }}
          >
            Clear All
          </Button>
        </Box>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>

      <AddStudentDialog
        open={addStudentOpen}
        onClose={() => setAddStudentOpen(false)}
        onStudentAdded={handleNewStudentAdded}
      />
    </Dialog>
  );
};

export default GroupEditDialog;
